import { useState, useRef, useEffect, useMemo } from 'react';

// Intl.supportedValuesOf is ES2022 — augment the type for ES2020 targets
// eslint-disable-next-line @typescript-eslint/no-namespace
declare global { namespace Intl { function supportedValuesOf(key: string): string[]; } }

// ── Helpers ──────────────────────────────────────────────────────────────────

interface TzEntry {
  iana: string;
  label: string;       // "Asia/Kolkata — IST (UTC+05:30)"
  region: string;       // "Asia"
  searchText: string;   // lowercase for filtering
}

function getOffsetLabel(tz: string): string {
  try {
    return (
      Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' })
        .formatToParts(new Date())
        .find(p => p.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
}

function getAbbreviation(tz: string): string {
  try {
    return (
      Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(new Date())
        .find(p => p.type === 'timeZoneName')?.value ?? ''
    );
  } catch {
    return '';
  }
}

function buildTzEntry(tz: string): TzEntry {
  const abbr = getAbbreviation(tz);
  const offset = getOffsetLabel(tz);
  const parts = [tz];
  if (abbr) parts.push(abbr);
  if (offset && offset !== abbr) parts.push(`(${offset})`);
  else if (offset) parts.push(`(${offset})`);
  const label = parts.length > 1 ? `${tz} \u2014 ${parts.slice(1).join(' ')}` : tz;
  const region = tz.includes('/') ? tz.split('/')[0] : 'Other';
  return { iana: tz, label, region, searchText: label.toLowerCase() };
}

// ── Styles ───────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px',
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', outline: 'none',
  boxSizing: 'border-box',
};

const dropdownStyle: React.CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
  maxHeight: 320, overflowY: 'auto',
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-md)',
  marginTop: 2,
};

const itemStyle: React.CSSProperties = {
  padding: '6px 12px', fontSize: 13, cursor: 'pointer',
  fontFamily: 'var(--font-body)', color: 'var(--text)',
};

const groupHeaderStyle: React.CSSProperties = {
  padding: '6px 12px', fontSize: 11, fontWeight: 700,
  fontFamily: 'var(--font-display)', color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  borderTop: '1px solid var(--border)', marginTop: 4,
  background: 'var(--bg)',
};

const detectedStyle: React.CSSProperties = {
  padding: '8px 12px', fontSize: 13, cursor: 'pointer',
  fontFamily: 'var(--font-body)', fontWeight: 600,
  color: '#059669', background: 'rgba(5,150,105,0.06)',
  borderBottom: '2px solid var(--border)',
};

// ── Component ────────────────────────────────────────────────────────────────

interface TimezoneDropdownProps {
  value: string;
  onChange: (tz: string) => void;
}

export default function TimezoneDropdown({ value, onChange }: TimezoneDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const detected = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ''; }
  }, []);

  const allEntries = useMemo(() => {
    try {
      return Intl.supportedValuesOf('timeZone').map(buildTzEntry);
    } catch {
      return [];
    }
  }, []);

  const detectedEntry = useMemo(
    () => (detected ? buildTzEntry(detected) : null),
    [detected],
  );

  // Filter entries based on search
  const filtered = useMemo(() => {
    if (!search) return allEntries;
    const q = search.toLowerCase();
    return allEntries.filter(e => e.searchText.includes(q));
  }, [allEntries, search]);

  // Group filtered entries by region
  const grouped = useMemo(() => {
    const groups: { region: string; entries: TzEntry[] }[] = [];
    let currentRegion = '';
    for (const entry of filtered) {
      if (entry.region !== currentRegion) {
        currentRegion = entry.region;
        groups.push({ region: currentRegion, entries: [] });
      }
      groups[groups.length - 1].entries.push(entry);
    }
    return groups;
  }, [filtered]);

  // Flat list for keyboard navigation (detected pinned + filtered)
  const flatItems = useMemo(() => {
    const items: TzEntry[] = [];
    if (detectedEntry && (!search || detectedEntry.searchText.includes(search.toLowerCase()))) {
      items.push(detectedEntry);
    }
    for (const entry of filtered) {
      if (entry.iana !== detectedEntry?.iana || search) {
        items.push(entry);
      }
    }
    // Deduplicate: if detected is in filtered and no search, skip it in filtered
    if (!search && detectedEntry) {
      return [detectedEntry, ...filtered.filter(e => e.iana !== detectedEntry.iana)];
    }
    return items;
  }, [filtered, detectedEntry, search]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const el = listRef.current.querySelector(`[data-tz-idx="${highlightIdx}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  const selectTz = (tz: string) => {
    onChange(tz);
    setOpen(false);
    setSearch('');
    setHighlightIdx(-1);
  };

  const displayValue = useMemo(() => {
    if (value) {
      const entry = allEntries.find(e => e.iana === value);
      return entry?.label ?? value;
    }
    if (detectedEntry) return detectedEntry.label;
    return '';
  }, [value, allEntries, detectedEntry]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
      e.preventDefault();
    } else if (e.key === 'ArrowDown') {
      setHighlightIdx(i => Math.min(i + 1, flatItems.length - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlightIdx(i => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === 'Enter' && highlightIdx >= 0 && highlightIdx < flatItems.length) {
      selectTz(flatItems[highlightIdx].iana);
      e.preventDefault();
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        style={inputStyle}
        value={open ? search : displayValue}
        onChange={e => { setSearch(e.target.value); setHighlightIdx(-1); }}
        onFocus={() => { setOpen(true); setSearch(''); }}
        onKeyDown={handleKeyDown}
        placeholder="Search timezones..."
        aria-expanded={open}
        aria-haspopup="listbox"
        role="combobox"
      />
      {open && (
        <div ref={listRef} style={dropdownStyle} role="listbox">
          {/* Detected timezone pinned at top */}
          {detectedEntry && (!search || detectedEntry.searchText.includes(search.toLowerCase())) && (
            <div
              data-tz-idx={0}
              style={{
                ...detectedStyle,
                background: highlightIdx === 0 ? 'rgba(5,150,105,0.12)' : detectedStyle.background,
              }}
              onMouseDown={e => { e.preventDefault(); selectTz(detectedEntry.iana); }}
              onMouseEnter={() => setHighlightIdx(0)}
              role="option"
              aria-selected={value === detectedEntry.iana}
            >
              {value === detectedEntry.iana ? '\u2713 ' : ''} Detected: {detectedEntry.label}
            </div>
          )}

          {/* Grouped timezone list */}
          {grouped.map(group => (
            <div key={group.region}>
              <div style={groupHeaderStyle}>{group.region}</div>
              {group.entries.map(entry => {
                const flatIdx = flatItems.findIndex(e => e.iana === entry.iana);
                const isHighlighted = flatIdx === highlightIdx;
                const isSelected = entry.iana === value;
                return (
                  <div
                    key={entry.iana}
                    data-tz-idx={flatIdx}
                    style={{
                      ...itemStyle,
                      background: isHighlighted
                        ? 'rgba(124,58,237,0.08)'
                        : isSelected
                          ? 'rgba(124,58,237,0.04)'
                          : 'transparent',
                      fontWeight: isSelected ? 600 : 400,
                    }}
                    onMouseDown={e => { e.preventDefault(); selectTz(entry.iana); }}
                    onMouseEnter={() => setHighlightIdx(flatIdx)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    {entry.label}
                  </div>
                );
              })}
            </div>
          ))}

          {filtered.length === 0 && (
            <div style={{ ...itemStyle, color: 'var(--text-secondary)', cursor: 'default' }}>
              No timezones match &ldquo;{search}&rdquo;
            </div>
          )}
        </div>
      )}
    </div>
  );
}
